import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaMoneyCheckAlt, FaPlus } from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  formatShortDate,
  PrimaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import { createCheckTemplate, listCheckTemplates } from "../../../utility/checkTemplates";

const pageLayoutLabel = (layout) =>
  layout === "threePerPage" ? "3 per page" : "Voucher / 3-part";

const OrganizationCheckTemplates = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { canManageTournaments } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentOrganization?.id) {
        setTemplates([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const rows = await listCheckTemplates(currentOrganization.id);
        if (!cancelled) setTemplates(rows);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Could not load check templates.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.id]);

  const handleCreate = async () => {
    if (!user || !currentOrganization?.id) return;
    setCreating(true);
    setError("");
    try {
      const template = await createCheckTemplate({
        organizationID: currentOrganization.id,
        userId: user.id,
      });
      navigate(`/organization/check-templates/${template.id}`);
    } catch (err) {
      console.error(err);
      setError("Could not create a check template. Try again.");
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" role="status" aria-label="Loading check templates">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={4} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Check templates
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              New templates start with a 3-part layout. Line the boxes up with
              your check stock, then print from Prizes.
            </p>
          </div>
          {canManageTournaments ? (
            <PrimaryButton
              onClick={handleCreate}
              icon={FaPlus}
              loading={creating}
              disabled={creating}
            >
              Create template
            </PrimaryButton>
          ) : null}
        </div>
      </header>

      {error ? (
        <InlineAlert type="error" title="There was a problem" message={error} />
      ) : null}

      {templates.length === 0 ? (
        <div className="card-board p-8 text-center">
          <FaMoneyCheckAlt className="mx-auto h-10 w-10 text-brand-muted mb-3" />
          <h2 className="heading-display text-lg mb-2">No check templates yet</h2>
          <p className="text-brand-muted mb-4">
            Create a template. It starts with fields on the check and both stubs.
            Upload a blank check as a guide and line the boxes up with your stock.
          </p>
          {canManageTournaments ? (
            <PrimaryButton onClick={handleCreate} icon={FaPlus} loading={creating}>
              Create template
            </PrimaryButton>
          ) : null}
        </div>
      ) : (
        <div className="card-board overflow-hidden">
          <ul className="divide-y divide-brand-charcoal/10">
            {templates.map((template) => (
              <li key={template.id}>
                <Link
                  to={`/organization/check-templates/${template.id}`}
                  className="block px-4 py-4 sm:px-6 hover:bg-brand-gold/10"
                >
                  <div className="text-sm font-medium text-brand-charcoal">
                    {template.name}
                  </div>
                  <div className="text-sm text-brand-muted">
                    {pageLayoutLabel(template.pageLayout)}
                    {template.updatedAt
                      ? ` · Updated ${formatShortDate(template.updatedAt)}`
                      : ""}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OrganizationCheckTemplates;
